import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import utc from 'dayjs/plugin/utc'
import dayOfYear from 'dayjs/plugin/dayOfYear'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { api } from './apiHelper'
import { Activity, ActivityStatus } from '../api/schemas/schema'
import { IDataContext } from '../interfaces/IDataContext'
import { handleMessage } from './stateHelper'
import { t } from 'i18next'
import { UseFormReset } from 'react-hook-form'
dayjs.extend(isoWeek)
dayjs.extend(utc)
dayjs.extend(dayOfYear)
dayjs.extend(weekOfYear)

export const getDaysInMonth = (year: number, month: any) => dayjs(`${year}-${month}-01`).daysInMonth()

export const quarters = (year: number) => [
   { name: 'Q1', value: getDaysInMonth(year, 1) + getDaysInMonth(year, 2) + getDaysInMonth(year, 3) },
   { name: 'Q2', value: getDaysInMonth(year, 4) + getDaysInMonth(year, 5) + getDaysInMonth(year, 6) },
   { name: 'Q3', value: getDaysInMonth(year, 7) + getDaysInMonth(year, 8) + getDaysInMonth(year, 9) },
   { name: 'Q4', value: getDaysInMonth(year, 10) + getDaysInMonth(year, 11) + getDaysInMonth(year, 12) },
]
export const months = (year: number) => {
   return [
      { name: t('Jan'), value: getDaysInMonth(year, 1) },
      { name: t('Feb'), value: getDaysInMonth(year, 2) },
      { name: t('Mar'), value: getDaysInMonth(year, 3) },
      { name: t('Apr'), value: getDaysInMonth(year, 4) },
      { name: t('May'), value: getDaysInMonth(year, 5) },
      { name: t('Jun'), value: getDaysInMonth(year, 6) },
      { name: t('Jul'), value: getDaysInMonth(year, 7) },
      { name: t('Aug'), value: getDaysInMonth(year, 8) },
      { name: t('Sep'), value: getDaysInMonth(year, 9) },
      { name: t('Oct'), value: getDaysInMonth(year, 10) },
      { name: t('Nov'), value: getDaysInMonth(year, 11) },
      { name: t('Dec'), value: getDaysInMonth(year, 12) },
   ]
}

export const weeks = (year: number) => {
   const weekData = []
   let day = dayjs(`${year}-01-01`)

   while (day.isBefore(dayjs(`${year}-12-31`)) || day.isSame(dayjs(`${year}-12-31:23:59:59:999`))) {
      let endOfWeek = day.endOf('isoWeek')

      if (endOfWeek.year() > year) {
         endOfWeek = dayjs(`${year}-12-31:23:59:59:999`)
      }

      let daysInWeek = endOfWeek.diff(day, 'day') + 1

      if (endOfWeek.isSame(dayjs(`${year}-12-31:23:59:59:999`))) {
         daysInWeek = endOfWeek.diff(day, 'day') + 1
      }
      const weekNumber = dayjs(day).isoWeek()

      weekData.push({
         number: weekNumber,
         value: daysInWeek,
      })

      day = day.endOf('isoWeek').add(1, 'day') // Move to the next week
   }

   if (weekData[0].number === 52 || weekData[0].number === 53) {
      weekData[0].number = null
   }

   if (weekData[weekData.length - 1].number === 1) {
      weekData[weekData.length - 1].number = null
   }
   return weekData
}
export const calendarStartAngle = (year: number) => {
   const firstDayOfYear = dayjs(`${year}-01-01`).day()
   const dayIndex = (firstDayOfYear - 1) * (360 / 365)
   return 90 + dayIndex
}

const FALLBACK_ACTIVITY_COLORS = ['#C3C8CF', '#74808F', '#9AA5B1', '#5B6672']

const CLUSTER_PROXIMITY_DAYS = 2 // activities within this many days of each other are treated as one visual cluster for labeling

// Groups activities whose date ranges overlap or lie within CLUSTER_PROXIMITY_DAYS of each other.
// Used to collapse dense groups of activity labels into a single "+N" badge (see ClusterCountBadge).
export const getActivityLabelClusters = (activities: Activity[], year: number): Map<string, string[]> => {
   const daysInYear = dayjs(`${year}`).isLeapYear() ? 366 : 365
   const ranges = (activities ?? []).map((activity) => {
      let startDay = dayjs(activity.startDate).dayOfYear()
      let endDay = dayjs(activity.endDate).dayOfYear()
      if (dayjs(activity.startDate).year() < year) startDay = 1
      if (dayjs(activity.endDate).year() > year) endDay = daysInYear
      return { id: activity.id, startDay, endDay }
   })

   const parent = new Map<string, string>()
   ranges.forEach((r) => parent.set(r.id, r.id))
   const find = (id: string): string => {
      let root = id
      while (parent.get(root) !== root) {
         root = parent.get(root)
      }
      return root
   }
   const union = (a: string, b: string) => {
      const rootA = find(a)
      const rootB = find(b)
      if (rootA !== rootB) parent.set(rootA, rootB)
   }

   for (let i = 0; i < ranges.length; i++) {
      for (let j = i + 1; j < ranges.length; j++) {
         const a = ranges[i]
         const b = ranges[j]
         const overlaps = a.startDay - CLUSTER_PROXIMITY_DAYS <= b.endDay && a.endDay + CLUSTER_PROXIMITY_DAYS >= b.startDay
         if (overlaps) union(a.id, b.id)
      }
   }

   const clusters = new Map<string, string[]>()
   ranges.forEach((r) => {
      const root = find(r.id)
      if (!clusters.has(root)) clusters.set(root, [])
      clusters.get(root).push(r.id)
   })
   return clusters
}

export const getPeriodDateRange = (
   periodType: 'quarter' | 'month' | 'week',
   periodValue: string | number,
   year: number
): { start: dayjs.Dayjs; end: dayjs.Dayjs; label: string } => {
   if (periodType === 'quarter') {
      const quarterIndex = Number(String(periodValue).replace('Q', '')) - 1
      const start = dayjs(`${year}-01-01`)
         .month(quarterIndex * 3)
         .startOf('month')
      const end = start.add(2, 'month').endOf('month')
      return { start, end, label: `${periodValue} ${year}` }
   }
   if (periodType === 'month') {
      // Accepts either a 1-12 month number (used by callers that keep the value in the URL, where a
      // translated name would break on language change) or a translated month name from months().
      const raw = String(periodValue)
      const monthIndex = /^[0-9]+$/.test(raw) ? Number(raw) - 1 : months(year).findIndex((m) => m.name === periodValue)
      const start = dayjs(`${year}-01-01`).month(monthIndex).startOf('month')
      const end = start.endOf('month')
      return { start, end, label: `${months(year)[monthIndex].name} ${year}` }
   }
   const weekNumber = Number(periodValue)
   const start = dayjs(`${year}-01-01`).isoWeek(weekNumber).startOf('isoWeek')
   const end = start.endOf('isoWeek')
   return { start, end, label: `${t('Week')} ${weekNumber}, ${year}` }
}

export const getActivitiesInPeriod = (activities: Activity[], start: dayjs.Dayjs, end: dayjs.Dayjs): Activity[] => {
   // Activity dates arrive without a timezone designator (the date picker stores local midnight,
   // which is sent as UTC and read back from datetime2 as Unspecified). formatDate renders them with
   // dayjs.utc(...).local(), so parse them the same way here - parsing them as local time instead
   // shifts a local midnight back onto the previous day and puts the activity in the wrong period.
   return (activities ?? []).filter(
      (a) => !dayjs.utc(a.startDate).local().isAfter(end) && !dayjs.utc(a.endDate).local().isBefore(start)
   )
}

export type ActivityPeriodType = 'quarter' | 'month' | 'week'

export const isActivityPeriodType = (value: string): value is ActivityPeriodType => value === 'quarter' || value === 'month' || value === 'week'

// Options for the period dropdowns. The value is deliberately language independent (Q1-Q4, month
// number 1-12, ISO week number) so a value kept in the URL survives a language change, while the
// text is translated. Weeks split across a year boundary are numbered null by weeks() and dropped.
export const getPeriodOptions = (periodType: ActivityPeriodType, year: number): { value: string; text: string }[] => {
   if (periodType === 'quarter') return quarters(year).map((q) => ({ value: q.name, text: q.name }))
   if (periodType === 'month') return months(year).map((m, i) => ({ value: String(i + 1), text: m.name }))
   return weeks(year)
      .filter((w) => w.number !== null)
      .map((w) => ({ value: String(w.number), text: `${t('Week')} ${w.number}` }))
}

// Guards against a hand-edited or stale URL: getPeriodDateRange would otherwise index past the end
// of months(year) and throw when reading the label.
export const isValidPeriodValue = (periodType: ActivityPeriodType, periodValue: string, year: number): boolean =>
   Boolean(periodValue) && getPeriodOptions(periodType, year).some((o) => o.value === periodValue)

export const stackOverlappingActivities = (activities: Activity[], year: number) => {
   const daysInYear = dayjs(`${year}`).isLeapYear() ? 366 : 365
   let stackedActivities: { name: string; title: string; startAngle: number; endAngle: number; heightOffset: number; color: string; zIndex: number }[] = []

   let activeLayers: { endDay: number; level: number }[] = []
   activities
      .sort((a, b) => dayjs(a.startDate).diff(dayjs(b.startDate)))
      .forEach((activity) => {
         let startDay = dayjs(activity.startDate).dayOfYear()
         let endDay = dayjs(activity.endDate).dayOfYear()
         if (dayjs(activity.startDate).year() < year) {
            startDay = 1
         }
         if (dayjs(activity.endDate).year() > year) {
            endDay = daysInYear
         }
         if (startDay === endDay || startDay - endDay < 3) {
            endDay = endDay + 1
         }
         const startAngle = calendarStartAngle(year) - (startDay / daysInYear) * 360
         const endAngle = calendarStartAngle(year) - (endDay / daysInYear) * 360

         let level = 0

         // eslint-disable-next-line no-loop-func
         while (activeLayers.some((layer) => layer.level === level && layer.endDay >= startDay)) {
            level++
         }

         activeLayers.push({ endDay, level })

         stackedActivities.push({
            name: activity.id,
            title: activity.title,
            startAngle,
            endAngle,
            heightOffset: level * 20,
            color: activity.colorHex || FALLBACK_ACTIVITY_COLORS[level % FALLBACK_ACTIVITY_COLORS.length],
            zIndex: -level,
         })
      })
   return stackedActivities
}

export const getActivities = async (
   dataContext: IDataContext,
   year: number,
   setActivities: React.Dispatch<React.SetStateAction<Activity[]>>,
   setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
   signal?: AbortSignal
) => {
   const { organisation } = dataContext.state
   setIsLoading(true)
   try {
      const res = (await api().getByOrganisationIdAndYear(String(organisation.id), String(year), { signal })).data

      if (res) {
         setActivities(res)
      }
   } catch (error: any) {
      if (error?.name === 'AbortError') return
      handleMessage(dataContext.setRootState, 'error', t('CouldNotFetchItem', { Item: t('Activities') }))
   } finally {
      if (!signal?.aborted) setIsLoading(false)
   }
}

export const onActivitySubmit = async (
   dataContext: IDataContext,
   data: Activity,
   setActivities: React.Dispatch<React.SetStateAction<Activity[]>>,
   onClose: () => void,
   currentYear: number,
   selectedActivity?: Activity
) => {
   const { organisation, userAccount } = dataContext.state
   try {
      let activity: Activity
      const isOneDayActivity = data.endDate === ''
      if (isOneDayActivity) {
         data.endDate = data.startDate
      }
      if (selectedActivity) {
         data.id = selectedActivity.id
         data.organisationId = selectedActivity.organisationId
         data.modifiedById = userAccount.id
         activity = (await api().updateActivity(data)).data
         const startYear = dayjs(activity.startDate).year()
         const endYear = dayjs(activity.endDate).year()
         const belongsToYear = startYear <= currentYear && endYear >= currentYear
         if (belongsToYear) {
            setActivities((prev) => prev.map((act) => (act.id === activity.id ? activity : act)))
         } else {
            setActivities((prev) => prev.filter((act) => act.id !== activity.id))
         }
      } else {
         data.organisationId = organisation.id
         data.modifiedById = userAccount.id
         data.createdById = userAccount.id
         activity = (await api().addActivity(data)).data
         setActivities((prev) => [...prev, activity])
      }
      onClose()
      handleMessage(dataContext.setRootState, 'success', t(selectedActivity ? 'ItemSuccessfullyUpdated' : 'ItemSuccessfullySaved', { Item: t('Activity') }))
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', t(selectedActivity ? 'CouldNotUpdateItem' : 'CouldNotCreateItem', { Item: t('Activity') }))
   }
}

export const deleteActivity = async (activityId: string, dataContext: IDataContext, setActivities: React.Dispatch<React.SetStateAction<Activity[]>>) => {
   try {
      await api().deleteActivity(activityId)
      setActivities((prev) => prev.filter((act) => act.id !== activityId))

      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyDeleted', { Item: t('Activity') }))
   } catch (error: any) {
      let errorMessage = t('CouldNotDeleteItem', { Item: t('Activity') })
      switch (error.status) {
         case 400:
            errorMessage = t('NoMatchingItemFound', { Item: t('Activity').toLowerCase() })
            break
         case 403:
            errorMessage = t('UserNotAllowedToActionItem', { Action: t('Delete').toLowerCase(), Item: t('Activity').toLowerCase() })
            break
      }
      handleMessage(dataContext.setRootState, 'error', t('CouldNotDeleteItem', { Item: t('Activity') }), errorMessage)
   }
}

export const sendActivityReminder = async (activityId: string, dataContext: IDataContext) => {
   try {
      await api().sendActivityReminder(activityId)

      handleMessage(dataContext.setRootState, 'success', t('ReminderSent'))
   } catch (error: any) {
      let errorMessage = t('CouldNotSendReminder')
      switch (error.status) {
         case 400:
            errorMessage = t('ActivityHasNoResponsible')
            break
         case 403:
            errorMessage = t('OnlySystemAdministratorCanSendReminders')
            break
         case 404:
            errorMessage = t('NoMatchingItemFound', { Item: t('Activity').toLowerCase() })
            break
      }
      handleMessage(dataContext.setRootState, 'error', t('CouldNotSendReminder'), errorMessage)
   }
}

/**
 * Marks an activity as completed, or reopens it. The status is deliberately not part of the activity
 * form - it is only ever changed from the menu, so there is a single way in and a single confirmation.
 */
export const setActivityStatus = async (
   activity: Activity,
   status: ActivityStatus,
   dataContext: IDataContext,
   setActivities: React.Dispatch<React.SetStateAction<Activity[]>>
) => {
   const { userAccount } = dataContext.state
   try {
      const updated = (await api().updateActivity({ ...activity, status, modifiedById: userAccount.id })).data
      setActivities((prev) => prev.map((act) => (act.id === updated.id ? updated : act)))
      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyUpdated', { Item: t('Activity') }))
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotUpdateItem', { Item: t('Activity') }))
   }
}

// The one filter value that is not an ActivityStatus: show ongoing and completed together.
export const ACTIVITY_STATUS_ALL = 'all'

/**
 * Reads the status filter out of the URL. No parameter means the default view, where completed
 * activities are hidden until someone asks for them. A stale or hand-edited value falls back to that
 * same default rather than hiding everything.
 */
export const parseActivityStatusParam = (value: string | null): ActivityStatus | null => {
   if (value === ACTIVITY_STATUS_ALL) return null
   // Digits only - Number('') and Number('   ') are both 0, which would otherwise pass as Ongoing.
   if (value === null || !/^\d+$/.test(value)) return ActivityStatus.Ongoing
   const parsed = Number(value)
   return parsed === ActivityStatus.Completed ? parsed : ActivityStatus.Ongoing
}

/**
 * A null status means "show everything", which only happens when the user explicitly picks it.
 * Activities created before the status column existed come back without one, so they count as Ongoing.
 */
export const filterActivitiesByStatus = (activities: Activity[], status: ActivityStatus | null): Activity[] =>
   status === null ? activities : activities.filter((activity) => (activity.status ?? ActivityStatus.Ongoing) === status)

export const getDefaultValues = (reset: UseFormReset<Activity>, activity?: Activity) => {
   reset({
      title: activity?.title || '',
      description: activity?.description || '',
      startDate: activity?.startDate || '',
      endDate: activity?.endDate || '',
      colorHex: activity?.colorHex || '',
      responsible: activity?.responsible ?? null,
      responsibleId: activity?.responsibleId ?? null,
      // onActivitySubmit PUTs the whole object, so a field missing here would be sent as undefined and
      // silently reset to the C# default - i.e. a completed activity would reopen on any ordinary edit.
      status: activity?.status ?? ActivityStatus.Ongoing,
   })
}
